/**
 * Event messages Sentry should drop before sending.
 *
 * Each entry is a browser or vendor telling us about its own internal state,
 * not a fault in this app. They are matched by `Sentry.init`'s `ignoreErrors`,
 * which tests each pattern against the event message and against the
 * `"{type}: {value}"` string of its exception.
 *
 * **Match the message, not the error type.** Every class below shares its
 * constructor name with an error that IS actionable, so a bare type match would
 * silently swallow real failures — the specific adjacent error is named on each
 * entry, and `sentryNoiseFilters.test.ts` pins that it still reports.
 *
 * This is deliberately not a stack-frame filter in `beforeSend`. Frame filtering
 * can only work for a dependency loaded as its own script from a vendor origin,
 * whose URL survives into the browser-side event. Everything here is bundled by
 * Vite, and `beforeSend` runs client-side — Sentry resolves source maps
 * server-side at ingest — so these frames still carry our own minified chunk
 * URL and name no vendor at all. Filtering bundled vendor noise has to key on
 * the message.
 *
 * Note these events are dropped by the `EventFilters` integration, which runs
 * *before* `beforeSend`, so the two mechanisms never interact. Dropped events
 * are not completely invisible: the SDK counts them in its client report under
 * the `event_processor` reason, so filtered volume is still visible in aggregate
 * on Sentry's Stats page even though no issue is created.
 */
export const SENTRY_IGNORED_ERROR_PATTERNS: readonly RegExp[] = [
  // View Transitions, from `document.startViewTransition` — which react-router
  // v8's `viewTransition` drives. The browser rejects the outstanding transition
  // when the document stops being able to run one: hidden, detaching, or no
  // longer fully active. (Blink raises a *different* error, `AbortError:
  // Transition was skipped`, when a second transition merely supersedes the
  // first, so this is not that case.) Either way nothing is broken and there is
  // no user-visible effect — the new view appears without the cross-fade.
  //
  // Scoped to this message so the unrelated IndexedDB `InvalidStateError`
  // ("Object store cannot be found in the database") still reports.
  /Transition was aborted because of invalid state/,

  // `@telnyx/webrtc` rejecting its own superseded socket request during
  // reconnect: it bumps a socket generation, then rejects every request from
  // the previous one. The library has already recovered by the time this
  // surfaces as an unhandled rejection.
  //
  // Matched on the full `(id=…, gen=…, current=…)` tuple rather than the bare
  // phrase. "Stale request cancelled" is ordinary English that any future
  // request-deduplication layer could plausibly emit, and unlike the other two
  // entries it has no vendor namespace or engine-generated wording to anchor
  // on; the tuple is the part only this library mints.
  /Stale request cancelled \(id=[^)]+, gen=\d+, current=\d+\)/,

  // Firebase reporting that the browser lacks the APIs push messaging needs.
  // That is a capability report, not a failure: the app degrades to no push
  // notifications by design.
  //
  // This one is a stopgap, not the fix. `FirebaseMessagingWeb`'s constructor
  // calls `getMessaging()` inside an unguarded `.then`, so on an unsupported
  // browser it produces an unhandled rejection the moment the module is
  // imported — and `@/mobile/pushNotifications` is imported at module scope by
  // `router/authLoader`, so every web visitor pays it (and carries
  // `firebase/messaging` in the entry chunk). Gating that import behind
  // `Capacitor.isNativePlatform()` would remove the error class at its source;
  // this pattern also covers stale bundles, so keep it either way. See SD-13244.
  //
  // Scoped to this Firebase error code so genuine `FirebaseError`s — auth
  // failures, service outages — still report.
  /messaging\/unsupported-browser/,
];
